import { Box, Button, Chip, Typography } from '@mui/material'
import {
  DataGridPro,
  GridColDef,
  GridPaginationModel,
  GridRowModel,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid-pro'
import { ReactElement, useMemo, useState } from 'react'
import { FilteredFactsheetResponseData } from 'src/@types/graphql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { ROUTES } from 'src/routing/routes'
import { usePermissionsForFactsheetsIndexPage } from 'src/service/security/PermissionHook'
import { DataGridContainer } from 'src/shared/grid/DataGridContainer'
import { DownloadIcon } from 'src/shared/icons/Icons'
import { HtmlRenderer } from 'src/shared/presentation/HtmlRenderer'
import { S } from 'src/shared/styled/S'
import { useGridTranslateHook } from 'src/shared/utils/hooks/grid-translate-hook'
import { useDelayedNavigate } from 'src/shared/utils/hooks/navigation-hooks'
import styled from 'styled-components'

const DataGridProStyled = styled(DataGridPro)`
  & .MuiDataGrid-row {
    &:hover {
      cursor: pointer;
    }
  }
`

interface Props {
  factsheets: FilteredFactsheetResponseData[]
  onDownloadHandle: () => void
  factsheetsCount: number | undefined
}

export const FactsheetsIndexGrid = ({ factsheets, onDownloadHandle, factsheetsCount }: Props): ReactElement => {
  const navigate = useDelayedNavigate()
  const gridTranslations = useGridTranslateHook()
  const gridRef = useGridApiRef()
  const { getMessage } = useMessageSource()
  const [loadingDownload, setLoadingDownload] = useState(false)
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'id',
      sort: 'desc',
    },
  ])
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({ page: 0, pageSize: 10 })

  const { canDownloadReport, canViewAllFactsheets } = usePermissionsForFactsheetsIndexPage()

  const rows = useMemo(() => {
    return factsheets.map((factsheet) => {
      return {
        ...factsheet,
        published: getMessage(`label.factsheet.published.${factsheet.published}`),
      }
    })
  }, [factsheets, getMessage])

  const handleRowClick = (params: GridRowModel) => {
    const factsheetId = params?.id
    navigate(ROUTES.FactsheetDescriptionRoot.nested.FactsheetDescription.params({ factsheetId }))
  }

  const columns = useMemo<GridColDef[]>(
    () => [
      {
        field: 'id',
        flex: 0.5,
        headerName: getMessage('label.id'),
        renderCell: ({ row }) => `${getMessage('label.factsheet.id.character')}${row.id}`,
      },
      {
        field: 'title',
        flex: 2,
        headerName: getMessage('label.title'),
      },
      {
        field: 'description',
        flex: 2,
        headerName: getMessage('label.description'),
        renderCell: ({ row }) => {
          return <HtmlRenderer html={row.description} truncate variant="body2" />
        },
      },
      {
        field: 'topics',
        flex: 1,
        headerName: getMessage('label.success.factor.topic'),
        renderCell: ({ row }) => {
          return row.topics?.map((m: string) => getMessage(`${m}`)).join(', ')
        },
      },
      {
        field: 'modules',
        flex: 1,
        headerName: getMessage('label.success.factor.life.phase'),
        renderCell: ({ row }) => {
          return row.modules?.map((m: string) => getMessage(`label.module.${m}`)).join(', ')
        },
      },
      {
        field: 'published',
        flex: 1,
        headerName: getMessage('label.factsheet.published'),
      },
    ],
    [getMessage],
  )

  const localHandleDownload = async () => {
    setLoadingDownload(true)
    await onDownloadHandle()
    setLoadingDownload(false)
  }

  return (
    <DataGridContainer>
      <DataGridProStyled
        rows={rows}
        columns={columns}
        apiRef={gridRef as any}
        localeText={gridTranslations}
        onRowClick={(props) => handleRowClick(props)}
        disableRowSelectionOnClick
        columnVisibilityModel={{
          published: canViewAllFactsheets,
        }}
        disableColumnFilter
        disableColumnReorder
        disableColumnSelector
        disableColumnPinning
        paginationModel={paginationModel}
        onPaginationModelChange={(model) => setPaginationModel(model)}
        pageSizeOptions={[10, 25, 50]}
        pagination
        sortModel={sortModel}
        onSortModelChange={(model) => setSortModel(model)}
        slots={{
          toolbar: () => (
            <S.DataGrid.ToolbarContainer>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="h3">{getMessage('label.factsheets.overview')}</Typography>
                {factsheetsCount !== undefined && (
                  <Chip
                    label={`${factsheetsCount} ${getMessage('label.number.of.factsheets')}`}
                    variant="filled"
                    color="secondary"
                    sx={{ marginLeft: 2 }}
                  />
                )}
              </Box>
              {canDownloadReport && (
                <Button
                  variant="text"
                  startIcon={<DownloadIcon />}
                  onClick={localHandleDownload}
                  loading={loadingDownload}
                >
                  {getMessage('button.download')}
                </Button>
              )}
            </S.DataGrid.ToolbarContainer>
          ),
        }}
      />
    </DataGridContainer>
  )
}
